import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Zap, Target, TrendingUp, Quote, CalendarDays } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";
import { BrandMarquee, SectionLabel } from "@/components/shared";
import { AGE_GROUPS, BENEFITS, TESTIMONIALS, GALLERY, HERO_IMAGE, SESSION_INFO } from "@/data/site";

function EnrollStrip() {
  return (
    <section className="relative z-10 border-y border-neon-green/40 bg-neon-green" data-testid="enroll-strip">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-10">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-2 font-heading text-sm uppercase tracking-widest text-black">
            <CalendarDays size={16} strokeWidth={2.5} /> Now Enrolling
          </span>
          <span className="font-body text-sm font-bold text-black/80">
            {SESSION_INFO.name} · {SESSION_INFO.start} · Mon &amp; Thu 5:00–6:00 PM · All ages 7–15
          </span>
        </div>
        <Link
          to="/register"
          data-testid="enroll-strip-cta"
          className="flex items-center gap-2 border-2 border-black px-4 py-2 font-heading text-xs uppercase tracking-widest text-black transition-colors hover:bg-black hover:text-neon-green"
        >
          Reserve Your Spot <ArrowUpRight size={14} strokeWidth={2.5} />
        </Link>
      </div>
    </section>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 160]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section className="relative flex min-h-screen items-end overflow-hidden pb-16 pt-32" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={HERO_IMAGE} alt="ARD youth soccer training" className="h-full w-full object-cover brightness-125 contrast-105 saturate-[1.15]" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/10" />
        <div className="absolute inset-0 bg-obsidian/15" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-neon-green" />
          <span className="font-body text-xs font-bold uppercase tracking-[0.3em] text-neon-green">
            DeSoto County, MS • Greater Memphis Area
          </span>
        </motion.div>

        <h1 className="font-heading text-6xl uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[9.5rem]">
          <MaskLines
            lines={["Own Every", <>Single <span className="text-neon-green neon-text-glow">Touch</span></>]}
            delay={0.15}
          />
        </h1>

        <div className="mt-10 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="max-w-md font-body text-base font-medium leading-relaxed text-white/70 sm:text-lg"
          >
            Elite soccer development for players ages 7–15. We build complete footballers —
            technically sharp, tactically smart, and relentlessly competitive.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="flex flex-wrap gap-4"
          >
            <Link
              to="/register"
              data-testid="hero-signup-btn"
              className="group relative flex items-center gap-2 overflow-hidden bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black"
            >
              <span className="relative z-10">Sign Up For Training</span>
              <ArrowUpRight size={18} strokeWidth={3} className="relative z-10 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/training"
              data-testid="hero-explore-btn"
              className="group flex items-center gap-2 border-2 border-white/20 px-8 py-4 font-heading text-sm uppercase tracking-widest text-white transition-colors hover:border-neon-green hover:text-neon-green"
            >
              Explore Programs
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="relative z-10 bg-obsidian py-16 md:py-20" data-testid="intro">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionLabel>Who We Are</SectionLabel>
            <Reveal>
              <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">
                This Is <br /><span className="text-stroke">ARD Midsouth</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-6 flex justify-center">
                <img
                  src="/ard-logo.png"
                  alt="ARD Performance Midsouth"
                  className="w-56 object-contain drop-shadow-[0_0_35px_rgba(57,255,20,0.25)]"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <p className="font-body text-lg leading-snug text-white/70 md:text-2xl">
                ARD Midsouth Performance is an elite soccer training program built for the young players of
                DeSoto County and the greater Memphis area. We don't run generic drills — we develop
                footballers. Every session is engineered to sharpen technique, speed up decision-making,
                and raise the standard of every player who steps on our field.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-10">
                {[["3", "Age Programs"], ["7-15", "Player Ages"], ["100%", "Development Focus"]].map(([big, small]) => (
                  <div key={small}>
                    <div className="font-heading text-4xl text-neon-green sm:text-6xl">{big}</div>
                    <div className="mt-2 font-body text-xs uppercase tracking-[0.2em] text-white/50">{small}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function AgeGroups() {
  return (
    <section className="relative z-10 bg-obsidian py-12 md:py-16" data-testid="age-groups">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>Programs</SectionLabel>
            <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">
              Every Age.<br />Elevated.
            </h2>
          </div>
          <Link to="/training" className="group flex items-center gap-2 font-body text-sm font-bold uppercase tracking-widest text-neon-green">
            View All Programs
            <ArrowUpRight size={18} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {AGE_GROUPS.map((g, i) => (
            <Reveal
              key={g.id}
              delay={i * 0.1}
            >
              <Link
                to={`/training#${g.id}`}
                data-testid={`age-card-${g.id}`}
                className="group relative flex h-full min-h-[420px] flex-col justify-end overflow-hidden border border-white/10 p-8"
              >
                <img src={g.image} alt={g.title} className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-transparent" />
                <div className="relative z-10">
                  <span className="font-body text-xs font-bold uppercase tracking-[0.3em] text-neon-green">{g.tag}</span>
                  <h3 className="mt-2 font-heading text-3xl uppercase leading-none text-white sm:text-5xl">{g.label}</h3>
                  <p className="mt-3 max-w-md font-body text-sm text-white/70">{g.blurb}</p>
                </div>
                <div className="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center border border-white/20 text-white transition-colors group-hover:border-neon-green group-hover:bg-neon-green group-hover:text-black">
                  <ArrowUpRight size={22} strokeWidth={2.5} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  const icons = [Zap, Target, TrendingUp, Zap];
  return (
    <section className="relative z-10 bg-surface py-16 md:py-20" data-testid="benefits">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-14">
          <SectionLabel>Why ARD</SectionLabel>
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">The ARD Edge</h2>
        </div>
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={b.n} delay={i * 0.08} className="bg-obsidian">
                <div className="group h-full p-8 transition-colors hover:bg-zinc-900">
                  <div className="flex items-center justify-between">
                    <Icon size={28} strokeWidth={2.5} className="text-neon-green" />
                    <span className="font-heading text-4xl text-white/10">{b.n}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-2xl uppercase leading-none text-white">{b.title}</h3>
                  <p className="mt-3 font-body text-sm leading-relaxed text-white/60">{b.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GalleryStrip() {
  return (
    <section className="relative z-10 bg-obsidian py-16 md:py-20" data-testid="gallery-highlights">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>On The Pitch</SectionLabel>
            <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">Camp Highlights</h2>
          </div>
          <Link to="/camps" className="group flex items-center gap-2 font-body text-sm font-bold uppercase tracking-widest text-neon-green">
            View Full Gallery
            <ArrowUpRight size={18} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {GALLERY.map((src, i) => (
            <Reveal key={i} delay={(i % 5) * 0.08} className={`${i % 2 ? "md:mt-10" : ""}`}>
              <div className="group aspect-[3/4] overflow-hidden border border-white/10">
                <img
                  src={src}
                  alt={`Camp highlight ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="relative z-10 bg-surface py-12 md:py-16" data-testid="testimonials">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-10">
          <SectionLabel>The Word</SectionLabel>
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">Parents & Players</h2>
        </div>
        <div className="grid gap-6 md:mx-auto md:max-w-4xl md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex h-full flex-col border border-white/10 bg-obsidian p-8">
                <Quote size={32} strokeWidth={2.5} className="text-neon-green" />
                <p className="mt-6 flex-1 font-body text-base leading-relaxed text-white/80">"{t.quote}"</p>
                <div className="mt-8 border-t border-white/10 pt-5">
                  <div className="font-heading text-lg uppercase text-white">{t.name}</div>
                  <div className="font-body text-xs uppercase tracking-widest text-neon-green">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative z-10 overflow-hidden bg-neon-green py-20 md:py-28" data-testid="home-cta">
      <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-8 px-5 text-center md:px-10">
        <h2 className="font-heading text-5xl uppercase leading-[0.85] tracking-tight text-black sm:text-7xl lg:text-8xl">
          Ready To Level Up?
        </h2>
        <p className="max-w-xl font-body text-base font-medium text-black/70 sm:text-lg">
          Spots are limited across every age group. Lock in your player's place and start training with ARD Midsouth Performance.
        </p>
        <Link
          to="/register"
          data-testid="cta-signup-btn"
          className="group flex items-center gap-2 border-2 border-black bg-black px-10 py-5 font-heading text-lg uppercase tracking-widest text-neon-green transition-colors hover:bg-transparent hover:text-black"
        >
          Sign Up For Training
          <ArrowUpRight size={22} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EnrollStrip />
      <Intro />
      <BrandMarquee />
      <AgeGroups />
      <Benefits />
      <GalleryStrip />
      <Testimonials />
      <CTA />
    </div>
  );
}
